import React, { useState, useEffect } from "react";
import { Alert, Autocomplete, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { StringField } from "../ConfigFields/StringField";
import { useAuth } from "../../../../hooks/AuthProvider";
import { FORMS_FORMS } from "../../../../config/urls";
import { ColorsConstants } from "../../../../cssUtils/constants";

// Fields shown below the form picker, in the schema's own order (flow_id is the picker).
const OTHER_FIELDS_SKIPPED = ["flow_id"];

const SendWhatsAppFlowPreset = ({ schema, config, handleConfigChange }) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const request = async (url) => {
    const token = await getValidToken();
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      },
    });
    if (!response.ok) throw new Error("Request failed");
    return response.json();
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await request(`${FORMS_FORMS}?limit=999&status=PUBLISHED`);
        // Only forms that exist on Meta can be sent.
        setForms((data.results || []).filter((form) => form.meta_id));
      } catch (e) {
        setError("Could not load forms. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const selectedForm =
    forms.find((form) => form.meta_id === config["flow_id"]) || null;

  const handleFormChange = async (form) => {
    handleConfigChange("flow_id", form?.meta_id || "");
    if (!form) return;

    // A Flow opens on its first screen, so fill that in too (still editable below).
    try {
      const detail = await request(`${FORMS_FORMS}${form.id}/`);
      const firstScreen = detail?.form_json?.screens?.[0]?.id;
      if (firstScreen) handleConfigChange("screen", firstScreen);
    } catch (e) {
      setError("Could not read the form's first screen. Set the Screen field manually.");
    }
  };

  const properties = schema?.properties || {};
  const fieldKeys = (schema?.field_order || Object.keys(properties)).filter(
    (key) => !OTHER_FIELDS_SKIPPED.includes(key),
  );

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}

      <Autocomplete
        options={forms}
        loading={loading}
        value={selectedForm}
        onChange={(e, newValue) => handleFormChange(newValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Flow"
            variant="outlined"
            required
            helperText={
              config["flow_id"] && !selectedForm && !loading
                ? `Current Flow ID ${config["flow_id"]} is not one of your published forms.`
                : "Only published forms are listed."
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        getOptionLabel={(option) => option?.name || ""}
        renderOption={(props, option) => (
          <Stack
            {...props}
            key={option?.id}
            direction="column"
            gap={0.5}
            sx={{
              alignItems: "flex-start !important",
              textAlign: "left",
              width: "100%",
            }}
          >
            <Typography>{option?.name}</Typography>
            <Typography variant="body2" color={ColorsConstants.primaryGray40}>
              {option?.screens_count} screen{option?.screens_count === 1 ? "" : "s"}
            </Typography>
          </Stack>
        )}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        noOptionsText="No published forms found"
      />

      {fieldKeys.map((key) =>
        properties[key]?.type === "string" ? (
          <StringField
            key={key}
            fieldKey={key}
            schema={properties[key]}
            value={config[key]}
            required={(schema?.required || []).includes(key)}
            onChange={(val) => handleConfigChange(key, val)}
          />
        ) : null,
      )}
    </Stack>
  );
};

export default SendWhatsAppFlowPreset;
